import type { DecisioningCreativeComponentTriggerPropertiesHash, PreviewTriggerPropertiesHash } from "./types";

/**
 * Applies DS creative-component trigger properties to a preview or test-send request.
 *
 * DS Go emails resolve creatives via `{% decisioning_creative_component %}` tags and the
 * preview form's `api_trigger_properties` payload — not via DetectedPropertiesEditor /
 * custom-user `api_trigger_properties`. When DS preview is active, custom-user
 * `api_trigger_properties` are therefore always empty in practice; we replace that bucket with
 * the DS hash rather than deep-merging overlapping keys.
 *
 * Other custom-user trigger buckets (`event_properties`, `sms`, `context`, etc.) are spread
 * through when present.
 *
 * @param decisioningTriggerPropertiesHash DS `api_trigger_properties` payload.
 * @param customUserTriggerPropertiesHash Optional custom-user trigger properties.
 * @return Resolved trigger properties for the request, or undefined when both inputs are absent.
 */
export const applyDecisioningCreativeComponentTriggerPropertiesForRequest = (
  decisioningTriggerPropertiesHash?: DecisioningCreativeComponentTriggerPropertiesHash,
  customUserTriggerPropertiesHash?: Partial<PreviewTriggerPropertiesHash>
): Partial<PreviewTriggerPropertiesHash> | undefined => {
  if (!decisioningTriggerPropertiesHash) {
    if (!customUserTriggerPropertiesHash) {
      return undefined;
    }
    return customUserTriggerPropertiesHash;
  }

  if (!customUserTriggerPropertiesHash) {
    return decisioningTriggerPropertiesHash;
  }

  // DS hash follows; custom-user api_trigger_properties are unused in DS compositions (see above).
  return {
    ...customUserTriggerPropertiesHash,
    ...decisioningTriggerPropertiesHash,
  };
};
